/**
 * Quick-filter status chip values for the Manage Returns list (query param `status` / store field
 * `filter_status`). Lives in a dependency-free module so helpers and stores can import it without
 * circular imports (e.g. `rma-paged-list-store` must not be loaded before this enum is defined).
 * Wire values must stay aligned with the rms-api `RMA_STATUS_FILTER` allowlist.
 */

export enum RMA_QUICK_FILTERS {
    ALL = "all",
    OPEN = "open",
    PENDING = "pending",
    APPROVED = "approved",
    IN_TRANSIT = "in-transit",
    RECEIVED = "received",
    DONE = "done",
    REJECTED = "rejected",
    CANCELED = "canceled",
    SHIPMENT_REJECTED = "shipment_rejected",
    REQUIRE_ATTENTION = "require-attention",
    PENDING_MANAGER_APPROVAL_ONLY = "pending-manager-approval-only",
    PENDING_APPROVAL_ONLY = "pending-approval-only",
}

export interface IQuickFilterChip {
    id: RMA_QUICK_FILTERS;
    translationKey: string;
    requiresManagerApproval?: boolean;
}

/**
 * Catalog of the status chips offered by the redesigned sidebar, in display order.
 * `ALL` and `OPEN` are deliberately absent: they are legacy list-page values (`OPEN` is also the
 * on-load default of `filter_status`) that the redesigned page expresses through the
 * Open / Closed / All tabs (`status_group`) instead, so they never render as a selected chip.
 */
export const QUICK_FILTER_CHIPS: readonly IQuickFilterChip[] = [
    { id: RMA_QUICK_FILTERS.PENDING, translationKey: "PENDING" },
    {
        id: RMA_QUICK_FILTERS.PENDING_APPROVAL_ONLY,
        translationKey: "PENDING_APPROVAL_ONLY",
        requiresManagerApproval: true,
    },
    {
        id: RMA_QUICK_FILTERS.PENDING_MANAGER_APPROVAL_ONLY,
        translationKey: "PENDING_MANAGER_APPROVAL_ONLY",
        requiresManagerApproval: true,
    },
    { id: RMA_QUICK_FILTERS.APPROVED, translationKey: "APPROVED" },
    { id: RMA_QUICK_FILTERS.IN_TRANSIT, translationKey: "IN_TRANSIT" },
    { id: RMA_QUICK_FILTERS.RECEIVED, translationKey: "RECEIVED" },
    { id: RMA_QUICK_FILTERS.DONE, translationKey: "DONE" },
    { id: RMA_QUICK_FILTERS.REJECTED, translationKey: "REJECTED" },
    { id: RMA_QUICK_FILTERS.CANCELED, translationKey: "CANCELED" },
    { id: RMA_QUICK_FILTERS.SHIPMENT_REJECTED, translationKey: "SHIPMENT_REJECTED" },
    { id: RMA_QUICK_FILTERS.REQUIRE_ATTENTION, translationKey: "REQUIRE_ATTENTION" },
];

/** Whether a `filter_status` value is one of the sidebar status chips a merchant can select. */
export function isQuickFilterChip(status: string | undefined): boolean {
    return QUICK_FILTER_CHIPS.some(chip => chip.id === status);
}
